import type { LdtOrderUpdateOptions } from '@ldt/types/order';
import { OrderRepository } from 'src/api/order_repository';
import {
  CreateOrderingProviderInput,
  LdtOrderDetailDirectUploadLabTechFieldsFragment,
  LdtOrderDetailFieldsFragment,
  LdtOrderForReviseReportFieldsFragment,
  OrderingProviderFieldsFragment,
  OrderBaseFieldsFragment,
  PathologyFaxAction,
  PathologyLabFieldsFragment,
  OrderStatus,
  ReviseReportInput,
  FaxFieldsFragment,
  FaxType,
  UpdateOrderingProviderInput,
} from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { CursorPaginatedResponse } from 'src/types/pagination';

/**
 * Which order-detail selection set to fetch. Extensible for future
 * workflow/role-specific views.
 */
export enum LdtOrderDetailView {
  Default = 'default',
  DirectUploadLabTech = 'directUploadLabTech',
}

// LDT platform-specific repository interface
export interface LdtOrderRepository extends OrderRepository<LdtOrderDetailFieldsFragment> {
  readonly _platform: DistributionPlatform.Ldt;

  /**
   * Persists order edits. `options` carries client-only mutation fields (e.g. Tempus
   * CS save email flag) that are not part of the persisted Order model.
   */
  update(
    order: LdtOrderDetailFieldsFragment,
    options?: LdtOrderUpdateOptions,
  ): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Fetches an order by ID using the selection set for the given view.
   */
  getOrderById(
    id: string,
    options: { view: LdtOrderDetailView },
  ): Promise<
    | LdtOrderDetailFieldsFragment
    | LdtOrderDetailDirectUploadLabTechFieldsFragment
    | null
  >;

  /**
   * Gets ordering providers with optional search and cursor-based pagination.
   * LDT-specific method for managing ordering providers.
   */
  getOrderingProviders(params: {
    searchText?: string;
    cursor?: string | null;
    first?: number;
  }): Promise<CursorPaginatedResponse<OrderingProviderFieldsFragment>>;

  /**
   * Creates a new ordering provider.
   * LDT-specific method for creating ordering providers.
   */
  createOrderingProvider(
    input: CreateOrderingProviderInput,
  ): Promise<OrderingProviderFieldsFragment>;

  /**
   * Updates an existing ordering provider.
   * LDT-specific method for updating ordering providers.
   */
  updateOrderingProvider(
    input: UpdateOrderingProviderInput,
  ): Promise<OrderingProviderFieldsFragment>;

  /**
   * Deletes an ordering provider.
   * LDT-specific method for deleting ordering providers.
   */
  deleteOrderingProvider(id: string): Promise<void>;

  /**
   * Validates an ordering physician email address.
   * LDT-specific method for validating email addresses.
   */
  validateOrderingPhysicianEmail(
    email: string,
  ): Promise<{ isValid: boolean; isAlreadyUsed: boolean }>;

  /**
   * Gets pathology labs with optional search and cursor-based pagination.
   * LDT-specific method for managing pathology labs.
   */
  getPathologyLabs(params: {
    searchText?: string;
    cursor?: string | null;
    first?: number;
  }): Promise<CursorPaginatedResponse<PathologyLabFieldsFragment>>;

  /**
   * Sets an order's pathology lab.
   * LDT-specific method for updating an order's pathology lab.
   */
  updateOrderPathologyLab(
    orderId: string,
    pathologyLabSalesforceId: string,
  ): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Deletes a completed uploaded file.
   * LDT-specific method for deleting files that have been successfully uploaded.
   */
  deleteFile(fileId: string): Promise<void>;

  /**
   * Gets the download URL for an uploaded file for the specified file ID.
   * Returns a presigned URL for downloading the file.
   */
  getFileDownloadUrl(fileId: string): Promise<string>;

  /**
   * Updates an order's status to QUALITY_CONTROL_APPROVED.
   * LDT-specific method for approving orders in QC Pending status.
   * Allows for CS+Sales users to optionally fax pathology files.
   */
  updateOrderStatusQCApproved(params: {
    orderId: string;
    note?: string | null;
    pathologyFaxAction?: PathologyFaxAction | null;
    uploadedFileIds?: string[] | null;
  }): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Updates an order's status to a specified status.
   * @param orderId - The ID of the order to update
   * @param status - The new status for the order
   */
  updateOrderStatus(
    orderId: string,
    status: OrderStatus,
  ): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * QC approve slide image - creates AI job.
   * Used when order is in SLIDE_IMAGE_QUALITY_CONTROL_PENDING.
   */
  qcApproveSlideImage(orderId: string): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * QC reject slide image - deletes slide from bxlink and rolls order back to SLIDE_IMAGE_QUALITY_CONTROL_FAILED.
   * Used when order is in SLIDE_IMAGE_QUALITY_CONTROL_PENDING.
   */
  qcRejectSlideImage(
    orderId: string,
    reason: string,
  ): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Updates an order with rejected report(s) and sets the new status.
   * Used when an Artera user saves a QC Pending order that has an AI report:
   * saves order form data and reverts to Lab Processing or AI Processing.
   * @param order - The order with updated form data
   * @param revertStatus - The status to set (e.g. LabProcessing, AiProcessing)
   */
  updateRejectedOrder(
    order: LdtOrderDetailFieldsFragment,
    revertStatus: OrderStatus,
  ): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Faxes the test report to the pathology lab for the specified order.
   * @param orderId - The ID of the order
   */
  faxTestReportToPathologyLab(
    orderId: string,
  ): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Faxes the test report to the ordering provider for the specified order.
   * Changes the order status to ReportFaxPending.
   * @param orderId - The ID of the order
   */
  faxTestReportToOrderingProvider(
    orderId: string,
  ): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Shares the test report with external recipients by sending them an access link.
   * @param orderId - The ID of the order
   * @param emails - Array of recipient email addresses
   */
  shareTrr(
    orderId: string,
    emails: string[],
  ): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Gets order data for the revise report form.
   * @param orderId - The ID of the order
   */
  getOrderForReviseReport(
    orderId: string,
  ): Promise<LdtOrderForReviseReportFieldsFragment | null>;

  /**
   * Submits a report revision.
   * @param input - The revision input
   */
  reviseReport(input: ReviseReportInput): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Handles refaxing pathology files in the case of a failed fax.
   */
  refaxPathologyFiles(params: {
    orderId: string;
    pathologyFaxAction: PathologyFaxAction;
    note?: string | null;
    uploadedFileIds?: string[] | null;
  }): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Gets the most recent fax for an order by fax type (e.g. pathology files).
   * Used to pre-populate the fax dialog with previously selected files.
   */
  getMostRecentFax(
    orderId: string,
    faxType: FaxType,
  ): Promise<FaxFieldsFragment | null>;

  /**
   * Rejects a report and optionally reverts the order to a previous status.
   * @param orderId - The ID of the order
   * @param reason - Rejection reason (required)
   * @param newStatus - Optional status to revert to (e.g. QualityControlPending, LabProcessing); when order has revised report, backend determines status
   */
  rejectReport(
    orderId: string,
    reason: string,
    newStatus: OrderStatus | null,
  ): Promise<LdtOrderDetailFieldsFragment>;

  /**
   * Updates an order's billing hold status.
   * @param orderId - The ID of the order
   * @param hasBillingHold - Whether the order has a billing hold
   */
  updateBillingHold(
    orderId: string,
    hasBillingHold: boolean,
  ): Promise<LdtOrderDetailFieldsFragment>;
}

// Type guard for runtime checking
export function isLdtOrderRepository(
  repo: OrderRepository<OrderBaseFieldsFragment>,
): repo is LdtOrderRepository {
  return repo._platform === DistributionPlatform.Ldt;
}
